// ============================================================================
// SkillCard - "Player Rating" card (PokerSweepStars branded)
// ============================================================================
// 7 metrics each scaled 0-1000 as the user's percentile vs. the active player

// base, plus a composite overall Player Rating and world rank. Tap-to-flip
// toggles to a stats-detail back. Privacy enforced server-side: viewing
// another player's card requires they have publicSkillCard = true.
//
// Styled with the app's POKER_COLORS palette (darkTeal/cyan/gold) so it
// reads as a native PSS component rather than a CoinPoker import.
// ============================================================================
import React, { useEffect, useState } from 'react';
import {
  Box, Text, HStack, VStack, Spinner, Progress, Divider, Button, Avatar, Badge,
} from '@chakra-ui/react';
import { Trophy, RefreshCw } from 'lucide-react';
import gameApiService from '../../services/gameApiService';
import { POKER_COLORS } from '../../theme/designSystem';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';

interface SkillCardProps {
  userId?: string; // undefined = self
  onClose?: () => void;
}

const METRIC_LABELS: Array<{ key: keyof NonNullable<Awaited<ReturnType<typeof gameApiService.getSkillCard>>['scores']>; label: string }> = [
  { key: 'roi', label: 'ROI' },
  { key: 'itm', label: 'In The Money' },
  { key: 'winnings', label: 'Winnings' },
  { key: 'tournamentsPlayed', label: 'Tournaments Played' },
  { key: 'finalTables', label: 'Final Tables' },
  { key: 'firstRank', label: '1st Place Finishes' },
  { key: 'biggestScore', label: 'Biggest Cash' },
];

const formatSince = (ts: number | null | undefined): string => {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
};

// Match the default avatar used elsewhere in the app: DiceBear pixel-art
// seeded by userId. Keeps the rating card visually consistent with whatever
// character the user sees in their profile / at the table.
const defaultAvatarFor = (userId?: string): string => {
  const seed = userId || 'anonymous';
  const params = new URLSearchParams({
    seed,
    backgroundColor: 'transparent',
    size: '200',
  });
  return `https://api.dicebear.com/7.x/pixel-art/svg?${params.toString()}`;
};

const SkillCard: React.FC<SkillCardProps> = ({ userId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flipped, setFlipped] = useState(false);
  const [card, setCard] = useState<Awaited<ReturnType<typeof gameApiService.getSkillCard>> | null>(null);

  // When viewing our own card, prefer the live photoURL from AuthContext so
  // the avatar matches whatever the rest of the app is showing right now,
  // including a brand-new selection that hasn't propagated through the
  // /skillCard endpoint cache yet.
  const { userProfile } = useAuth();
  const isSelf = !userId || userId === auth.currentUser?.uid;
  const livePhotoURL = isSelf ? userProfile?.photoURL : undefined;

  const load = async () => {
    setLoading(true);
    setError(null);
    const r = await gameApiService.getSkillCard(userId);
    setLoading(false);
    if (!r.success) {
      setError(r.error || 'Failed to load rating card');
      return;
    }
    setCard(r);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  if (loading) {
    return (
      <Box p={8} textAlign="center"><Spinner color={POKER_COLORS.cyan} size="lg" /></Box>
    );
  }
  if (error) {
    return (
      <Box p={6} textAlign="center" bg={POKER_COLORS.cardBg} borderRadius="lg">
        <Text color="red.300" mb={3}>{error}</Text>
        {onClose && <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}
      </Box>
    );
  }
  if (!card) return null;

  // App-palette card surface: darkTeal → cardBg vertical gradient with cyan trim
  const cardGradient = `linear-gradient(160deg, ${POKER_COLORS.darkTeal} 0%, ${POKER_COLORS.cardBg} 60%, ${POKER_COLORS.darkBg} 100%)`;

  return (
    <Box
      bg={cardGradient}
      border="1px solid"
      borderColor={`${POKER_COLORS.cyan}33`}
      borderRadius="xl"
      overflow="hidden"
      maxW="480px"
      w="100%"
      mx="auto"
      boxShadow={`0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px ${POKER_COLORS.cyan}11 inset`}
    >
      {/* Header */}
      <Box
        bg="rgba(0,0,0,0.35)"
        px={5} py={3}
        borderBottom="1px solid"
        borderColor={`${POKER_COLORS.cyan}22`}
      >
        <HStack justify="space-between">
          <VStack align="start" spacing={0}>
            <Text fontSize="lg" fontWeight="bold" color="white">{card.username || 'Player'}</Text>
            {card.sinceDate && (
              <Text fontSize="xs" color="gray.400">Member since {formatSince(card.sinceDate)}</Text>
            )}
          </VStack>
          <HStack>
            <Button size="xs" variant="ghost" color={POKER_COLORS.cyan}
              onClick={load} leftIcon={<RefreshCw size={12} />}>Refresh</Button>
            {onClose && (
              <Button size="xs" variant="ghost" color="gray.400" onClick={onClose}>Close</Button>
            )}
          </HStack>
        </HStack>
      </Box>

      {!flipped ? (
        // ── FRONT ─────────────────────────────────────────────────────────
        <Box px={5} py={5}>
          <VStack spacing={4}>
            {card.worldRank && (
              <Badge
                bg="rgba(0,0,0,0.4)"
                color={POKER_COLORS.cyan}
                px={3} py={1}
                borderRadius="md"
                border={`1px solid ${POKER_COLORS.cyan}33`}
              >
                Global Rank: {card.worldRank.toLocaleString()}
                {card.sampleSize && card.sampleSize > 0 && (
                  <Text as="span" color="gray.500" ml={1}>
                    / {card.sampleSize.toLocaleString()}
                  </Text>
                )}
              </Badge>
            )}

            <VStack spacing={2}>
              <Text fontSize="xs" color="gray.400" letterSpacing="widest" fontWeight="semibold">
                PLAYER RATING
              </Text>
              {/* Avatar - uses the user's selected photoURL if present,
                  otherwise the same DiceBear pixel-art default the rest of
                  the app shows, so the rating card matches their character. */}
              <Avatar
                size="lg"
                src={livePhotoURL || card.photoURL || defaultAvatarFor(card.userId)}
                name={card.username}
                bg={POKER_COLORS.darkTeal}
                color={POKER_COLORS.cyan}
                border={`2px solid ${POKER_COLORS.cyan}66`}
              />
              <Box
                bg="rgba(0,0,0,0.55)"
                px={6} py={1.5}
                borderRadius="full"
                mt={1}
                border={`1px solid ${POKER_COLORS.gold}66`}
              >
                <Text
                  fontSize="3xl"
                  fontWeight="extrabold"
                  color={POKER_COLORS.gold}
                  letterSpacing="wider"
                  lineHeight="1"
                >
                  {(card.overallSkillScore ?? 0).toLocaleString()}
                </Text>
              </Box>
              <Text fontSize="2xs" color="gray.500" letterSpacing="wide">OUT OF 1000</Text>
            </VStack>

            <Divider borderColor={`${POKER_COLORS.cyan}22`} />

            <Box w="100%" bg="rgba(0,0,0,0.25)" borderRadius="md" p={3}>
              <VStack spacing={3} align="stretch">
                {METRIC_LABELS.map(m => {
                  const v = (card.scores as any)?.[m.key] ?? 0;
                  return (
                    <Box key={m.key}>
                      <HStack justify="space-between" mb={1}>
                        <Text fontSize="xs" color="gray.300">{m.label}</Text>
                        <Text fontSize="xs" color={POKER_COLORS.cyan} fontWeight="bold">{v}</Text>
                      </HStack>
                      <Progress
                        value={(v / 1000) * 100}
                        size="xs"
                        bg="rgba(255,255,255,0.06)"
                        sx={{ '& > div': { background: POKER_COLORS.cyan } }}
                        borderRadius="full"
                      />
                    </Box>
                  );
                })}
              </VStack>
              <Text fontSize="xs" color="gray.500" mt={3} textAlign="center">
                Scores are percentiles vs. the active player base · 0-1000

              </Text>
            </Box>

            <Button
              size="sm" variant="ghost" color={POKER_COLORS.cyan}
              onClick={() => setFlipped(true)}
            >
              Flip card · career stats
            </Button>
          </VStack>
        </Box>
      ) : (
        // ── BACK ──────────────────────────────────────────────────────────
        <Box px={5} py={5}>
          <VStack spacing={4} align="stretch">
            <HStack>
              <Trophy size={16} color={POKER_COLORS.gold} />
              <Text fontSize="sm" color="gray.300" fontWeight="semibold">Career stats</Text>
            </HStack>

            {card.hasStats ? (
              <VStack spacing={2} align="stretch">
                <StatRow label="Tournaments played" value={card.rawStats?.totalTournaments ?? 0} />
                <StatRow label="In the money finishes" value={card.rawStats?.itmCount ?? 0} />
                <StatRow label="Final tables" value={card.rawStats?.finalTableCount ?? 0} />
                <StatRow label="1st place finishes" value={card.rawStats?.firstPlaceCount ?? 0} />
                <Divider borderColor={`${POKER_COLORS.cyan}22`} />
                <StatRow label="Total buy-ins" value={(card.rawStats?.totalBuyIns ?? 0).toLocaleString()} />
                <StatRow label="Total winnings" value={(card.rawStats?.totalWinnings ?? 0).toLocaleString()} />
                <StatRow label="Biggest cash" value={(card.rawStats?.biggestScore ?? 0).toLocaleString()} accent />
                <Divider borderColor={`${POKER_COLORS.cyan}22`} />
                <StatRow
                  label="ROI"
                  accent
                  value={
                    (card.rawStats?.totalBuyIns ?? 0) > 0
                      ? `${(((card.rawStats!.totalWinnings - card.rawStats!.totalBuyIns) / card.rawStats!.totalBuyIns) * 100).toFixed(1)}%`
                      : '-'
                  }
                />
                <StatRow
                  label="ITM %"
                  value={
                    (card.rawStats?.totalTournaments ?? 0) > 0
                      ? `${((card.rawStats!.itmCount / card.rawStats!.totalTournaments) * 100).toFixed(1)}%`
                      : '-'
                  }
                />
              </VStack>
            ) : (
              <Text color="gray.400" fontSize="sm" textAlign="center" py={4}>
                No tournament history yet - play a tournament to populate your card.
              </Text>
            )}

            <Button
              size="sm" variant="ghost" color={POKER_COLORS.cyan}
              onClick={() => setFlipped(false)}
            >
              Flip card · rating
            </Button>
          </VStack>
        </Box>
      )}
    </Box>
  );
};

const StatRow: React.FC<{ label: string; value: number | string; accent?: boolean }> = ({ label, value, accent }) => (
  <HStack justify="space-between" px={2}>
    <Text fontSize="sm" color="gray.400">{label}</Text>
    <Text
      fontSize="sm"
      color={accent ? POKER_COLORS.gold : 'white'}
      fontWeight="bold"
    >
      {value}
    </Text>
  </HStack>
);

export default SkillCard;
